import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { IconArrowBoxRight } from "../../../common/icons/IconArrowBoxRight/IconArrowBoxRight";
import type { CsiTransactionHistoryEntry } from "../../../services/CustomerSalesIncentive/CustomerSalesIncentive.types";
import { formatDateKpi } from "../../../common/util/formatDate";
import { storedLocale } from "../../Authentication/util";
import defaultContent from "../../../content/csi";
import commonContent from "../../../content/common";
import "./TransactionHistory.scss";

type Props = {
  entries: CsiTransactionHistoryEntry[];
  isLoading: boolean;
  customerOverviewPath?: string;
};

const TransactionHistory = ({ entries, isLoading, customerOverviewPath }: Props) => {
  const { t } = useTranslation(["csi", "common"]);
  const locale = storedLocale();

  const dateHeader = t("transactionHistory.dateHeader", defaultContent.transactionHistory.dateHeader);
  const orderHeader = t("transactionHistory.orderNumberHeader", defaultContent.transactionHistory.orderNumberHeader);
  const pvHeader = t("transactionHistory.pvHeader", defaultContent.transactionHistory.pvHeader);
  const customerHeader = t("transactionHistory.aboNameHeader", defaultContent.transactionHistory.aboNameHeader);
  const confidentialUser = t("common:confidentialUser", commonContent.confidentialUser).toString();

  return (
    <div className="TransactionHistory">
      <div className="TransactionHistory__header">
        <LeafRectangleLoader isLoading={isLoading} height="1.5rem" width="12rem">
          <h3 className="TransactionHistory__title">
            {t("transactionHistory.title", defaultContent.transactionHistory.title)}
          </h3>
        </LeafRectangleLoader>
        {customerOverviewPath && (
          <Link to={customerOverviewPath} className="TransactionHistory__cml-link">
            {t("cmlLink", defaultContent.cmlLink)}
            <IconArrowBoxRight />
          </Link>
        )}
      </div>

      <LeafRectangleLoader isLoading={isLoading} height="10rem" width="100%">
        {entries.length === 0 ? (
          <div className="TransactionHistory__empty" aria-live="polite">
            {t("transactionHistory.emptyState", defaultContent.transactionHistory.emptyState)}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="TransactionHistory__table-wrapper">
              <table
                className="TransactionHistory__table"
                aria-label={t("transactionHistory.title", defaultContent.transactionHistory.title)}
              >
                <caption className="TransactionHistory__caption">
                  {t("transactionHistory.title", defaultContent.transactionHistory.title)}
                </caption>
                <thead>
                  <tr className="TransactionHistory__header-row">
                    <th scope="col">{customerHeader}</th>
                    <th scope="col">{dateHeader}</th>
                    <th scope="col">{orderHeader}</th>
                    <th scope="col">{pvHeader}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr
                      key={entry.OrderNumber}
                      className={`TransactionHistory__row${index === entries.length - 1 ? " TransactionHistory__row--last" : ""}`}
                    >
                      <td>
                        <span className="TransactionHistory__customer-name">
                          {entry.AboName && !entry.PrivacyFlag ? entry.AboName : confidentialUser}
                        </span>
                        {entry.AboNo && !entry.PrivacyFlag && (
                          <span className="TransactionHistory__customer-abo"> #{entry.AboNo}</span>
                        )}
                      </td>
                      <td>{formatDateKpi(entry.OrderDate, locale)}</td>
                      <td>#{entry.OrderNumber}</td>
                      <td>
                        {entry.TotalPV.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div
              className="TransactionHistory__mobile-list"
              aria-label={t("transactionHistory.title", defaultContent.transactionHistory.title)}
            >
              {entries.map((entry) => (
                <div key={entry.OrderNumber} className="TransactionHistory__card">
                  <p className="TransactionHistory__card-name">
                    {entry.AboName && !entry.PrivacyFlag ? entry.AboName : confidentialUser}
                    {entry.AboNo && !entry.PrivacyFlag && (
                      <span className="TransactionHistory__card-abo"> #{entry.AboNo}</span>
                    )}
                  </p>
                  <p className="TransactionHistory__card-date">{formatDateKpi(entry.OrderDate, locale)}</p>
                  <p className="TransactionHistory__card-order">#{entry.OrderNumber}</p>
                  <p className="TransactionHistory__card-pv">
                    {entry.TotalPV.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                    {pvHeader}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </LeafRectangleLoader>
    </div>
  );
};

export default TransactionHistory;
